import React, { useContext, useEffect, useState } from 'react';
import { StateContext, User } from '../../StateContext';
import { useLanguage } from '../../Services/LanguageContext';
import {
    getAdminUsersCall,
    editAdminUserCall,
    unlockAdminUserCall,
    deleteAdminUserCall,
} from '../../Services/APICalls/apicalls';
import { Modal, Box, TextField, Switch, FormControlLabel } from '@mui/material';
import { boxStyle } from '../Projects/styles';
import DeleteConfirmation from '../../General/DeleteConfirmation';

const UserManagement = () => {
    const { loginData } = useContext(StateContext);
    const { t } = useLanguage();

    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const [editingUser, setEditingUser] = useState<User | null>(null);
    const [editName, setEditName] = useState('');
    const [editIsAdmin, setEditIsAdmin] = useState(false);

    const [deletingUserId, setDeletingUserId] = useState<string | null>(null);

    const loadUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await getAdminUsersCall();
            setUsers(data);
        } catch {
            setError(t('couldNotLoadUsers'));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadUsers();
    }, []);

    const openEdit = (user: User) => {
        setEditingUser(user);
        setEditName(user.userName);
        setEditIsAdmin(user.isAdmin);
    };

    const handleSaveEdit = () => {
        if (!editingUser) return;
        editAdminUserCall(editingUser.id, editName, editIsAdmin, () => {
            setEditingUser(null);
            loadUsers();
        });
    };

    const handleUnlock = (user: User) => {
        unlockAdminUserCall(user.id, loadUsers);
    };

    const handleDeleteConfirm = () => {
        if (!deletingUserId) return;
        deleteAdminUserCall(deletingUserId, () => {
            setDeletingUserId(null);
            loadUsers();
        });
    };

    if (loading) return <div className='admin-loading'>{t('loadingUsers')}</div>;
    if (error) return <div className='admin-error'>{error}</div>;
    if (users.length === 0) return <div className='admin-empty'>{t('noUsers')}</div>;

    const currentUserId = loginData?.user?.id;

    return (
        <>
            <div className='user-list'>
                {users.map(user => (
                    <div key={user.id} className='user-row'>
                        <div className='user-info'>
                            <div className='user-avatar'>
                                {user.userName[0]?.toUpperCase()}
                            </div>
                            <div>
                                <div className='user-name'>{user.userName}</div>
                                <div className='user-badges'>
                                    {user.isAdmin && (
                                        <span className='badge badge-admin'>Admin</span>
                                    )}
                                    {user.lockoutEnabled && user.accessFailedCount >= 5 && (
                                        <span className='badge badge-locked'>
                                            {t('accountLocked')}
                                        </span>
                                    )}
                                    {user.accessFailedCount > 0 && (
                                        <span className='badge badge-failed'>
                                            {user.accessFailedCount} {t('failedLogins')}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>

                        <div className='user-actions'>
                            <button className='action-btn' onClick={() => openEdit(user)}>
                                {t('edit')}
                            </button>
                            {user.accessFailedCount > 0 && (
                                <button className='action-btn' onClick={() => handleUnlock(user)}>
                                    {t('unlockAccount')}
                                </button>
                            )}
                            {user.id !== currentUserId && (
                                <button
                                    className='action-btn danger'
                                    onClick={() => setDeletingUserId(user.id)}
                                >
                                    {t('delete')}
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            <Modal open={editingUser !== null} onClose={() => setEditingUser(null)}>
                <Box style={boxStyle}>
                    <h2
                        style={{
                            margin: '0 0 24px',
                            fontSize: '20px',
                            fontWeight: 700,
                            color: 'var(--text-primary)',
                        }}
                    >
                        {t('editUser')}
                    </h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                        <TextField
                            label={t('username')}
                            value={editName}
                            onChange={e => setEditName(e.target.value)}
                            fullWidth
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={editIsAdmin}
                                    onChange={e => setEditIsAdmin(e.target.checked)}
                                    disabled={editingUser?.id === currentUserId}
                                />
                            }
                            label={t('isAdmin')}
                            style={{ color: 'var(--text-primary)' }}
                        />
                        <button style={{ width: '100%' }} onClick={handleSaveEdit}>
                            {t('save')}
                        </button>
                    </div>
                </Box>
            </Modal>

            <DeleteConfirmation
                dialogOpen={deletingUserId !== null}
                handleDialogConfirm={handleDeleteConfirm}
                handleDialogAbort={() => setDeletingUserId(null)}
            />
        </>
    );
};

export default UserManagement;
